import { VaultDetailPanel } from "@/components/vault/VaultDetailPanel";
import type { VaultActionKind } from "@/components/vault/VaultDetailPanel";
import { ROUTES } from "@/lib/routes";
import { ArrowLeft } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";

const ACTIONS: VaultActionKind[] = ["deposit", "mint", "withdraw", "repay"];

function parseAction(raw: string | null): VaultActionKind {
	if (!raw) return null;
	const a = raw.toLowerCase();
	if ((ACTIONS as string[]).includes(a)) return a as Exclude<VaultActionKind, null>;
	return null;
}

/**
 * Full-page vault management (V1-inspired structure, V2 design).
 * No popup — deep-linkable, shareable, room for gauge + actions.
 */
export default function VaultDetailPage() {
	const { vaultId } = useParams<{ vaultId: string }>();
	const [params] = useSearchParams();
	const initialAction = parseAction(params.get("action"));

	if (!vaultId) {
		return (
			<div className="container mx-auto px-4 py-12 text-center">
				<p className="text-sm text-muted-foreground">Missing vault id.</p>
				<Link
					to={ROUTES.vaults}
					className="btn-primary mt-4 inline-flex px-5 text-sm"
				>
					Back to vaults
				</Link>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-3xl space-y-6 px-4 py-6 sm:py-8">

			<div className="flex flex-wrap items-center gap-3">
				<Link
					to={ROUTES.vaults}
					className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-xl border border-border bg-card"
					aria-label="Back to vaults"
				>
					<ArrowLeft className="size-5" />
				</Link>
				<div>
					<p className="section-eyebrow">Vault #{vaultId}</p>
					<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
						Manage position
					</h1>
					<p className="text-sm text-muted-foreground">
						Deposit, mint, withdraw, or repay — full page, not a popup.
					</p>
				</div>
			</div>

			<div className="card-surface p-4 sm:p-6">
				<VaultDetailPanel
					vaultId={vaultId}
					initialAction={initialAction}
					compact={false}
				/>
			</div>
		</div>
	);
}
